import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type RefObject,
} from "react";
import { useReducedMotion } from "framer-motion";
import { PRACTICE_LOOP, type LoopStage } from "./loop-constants";

/**
 * The clock behind the landing page's practice loop.
 *
 * The hero demo and the loop diagram sit a full screen apart, but they
 * are two views of the *same* loop — so they share one stage index and
 * one timer. Two independent timers would drift, and a visitor scrolling
 * from one to the other would see the product contradicting itself.
 *
 * Three things stop the clock, because an animation nobody asked for
 * should cost nothing:
 *
 *   1. Nothing is on screen. Consumers report their own visibility
 *      (`useLoopVisibility`), so a skipped hero idles.
 *   2. The tab is in the background.
 *   3. The visitor asked for reduced motion — the loop then holds a
 *      single stage and the page reads as a normal, static one.
 *
 * `seek()` is how a person takes over: clicking a stage in the diagram
 * jumps there and restarts its dwell, so the demo answers the click
 * immediately instead of waiting out whatever it was showing.
 *
 * The provider component that drives all of this lives in
 * `LoopClockProvider.tsx`; only the hooks are here.
 */

export interface LoopApi {
  stages: LoopStage[];
  index: number;
  stage: LoopStage;
  seek: (index: number) => void;
  /** True while the clock is running — consumers may pause on hover. */
  running: boolean;
  setPaused: (paused: boolean) => void;
  reportVisible: (visible: boolean) => void;
}

export const LoopClockContext = createContext<LoopApi | null>(null);

export function useLoopClock(): LoopApi {
  const api = useContext(LoopClockContext);
  if (!api) {
    throw new Error("useLoopClock must be used inside <LoopClockProvider>");
  }
  return api;
}

/**
 * Tells the clock when this element is on screen. Attach the returned
 * ref to the outermost node of a loop view.
 */
export function useLoopVisibility<T extends HTMLElement>(): RefObject<T> {
  const ref = useRef<T>(null);
  const { reportVisible } = useLoopClock();

  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    const observer = new IntersectionObserver(
      ([entry]) => reportVisible(entry.isIntersecting),
      { threshold: 0.12 },
    );
    observer.observe(element);
    return () => {
      observer.disconnect();
      reportVisible(false);
    };
  }, [reportVisible]);

  return ref;
}

/** Shared bookkeeping behind the provider — exported so it can be tested. */
export function useLoopState() {
  const reduceMotion = useReducedMotion();
  const [index, setIndex] = useState(0);
  const [userPaused, setUserPaused] = useState(false);
  const [tabHidden, setTabHidden] = useState(false);
  const [visibleCount, setVisibleCount] = useState(0);

  const stages = PRACTICE_LOOP;

  useEffect(() => {
    const onVisibility = () => setTabHidden(document.hidden);
    onVisibility();
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, []);

  const running =
    !reduceMotion && !userPaused && !tabHidden && visibleCount > 0;

  useEffect(() => {
    if (!running) return;
    const timeout = window.setTimeout(
      () => setIndex((i) => (i + 1) % stages.length),
      stages[index].dwell,
    );
    return () => window.clearTimeout(timeout);
  }, [running, index, stages]);

  const seek = useCallback(
    (next: number) => {
      setIndex(((next % stages.length) + stages.length) % stages.length);
    },
    [stages.length],
  );

  // Stable identity: consumers register visibility in an effect, and an
  // identity that changed on every count change would re-run the observer.
  const reportVisible = useCallback((visible: boolean) => {
    setVisibleCount((count) => (visible ? count + 1 : Math.max(0, count - 1)));
  }, []);

  return {
    stages,
    index,
    stage: stages[index],
    seek,
    running,
    setPaused: setUserPaused,
    reportVisible,
  };
}
